import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Text } from '@Sage/hoi-poi-ui';
import Context from 'managers/Context';
import { logEvent } from 'utils/tracking';
import { getNumberAsCurrency } from 'utils/currency';
import { getNumberAsLocaleNumber } from 'utils/numbers';
import { getBackendBoolean } from 'utils/fm';
import { getMomentFromDateBackend, formatDateWithTimeOrNot } from 'utils/dates';
import { getLiteral } from 'utils/getLiteral';
import TextWithLinks from 'components/TextWithLinks';
import { EntityChip } from 'containers/components/chips';

/**
 * Shared logic for featured fields: data fetching, sections transformation,
 * modal state, featured selection, and API (setRelevantFields).
 * Used by CompanyDetailWidget and OpportunityDetailWidget (button in footer + MoreFieldsModal).
 */
export function useFeaturedFields({
    entity,
    entityId,
    entityExtrafields = [],
    data,
    setDynamicAsHidden,
    extraFieldsSchemaMap = {},
}) {
    const [allExtraFields, setExtraFields] = useState(entityExtrafields);
    const [featuredIds, setFeaturedFieldsIds] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const featuredFieldsRef = useRef([]);

    const renderMultipleValueList = useCallback((value) => {
        if (!value) return null;
        const values = value.trim().split(';').join(', ');
        return values ? (
            <span className="featured-fields__multiple-list-values">{values}</span>
        ) : null;
    }, []);

    const renderTextValue = useCallback((value) => {
        if (!value) return null;
        return (
            <TextWithLinks
                classNames={['fm-extra-fields-widget-row-value__text-field']}
                text={value}
            />
        );
    }, []);

    const finalSections = useMemo(() => {
        return allExtraFields?.map((section) => {
            const finalSection = { ...section, description: section.descripcion };
            const sectionData = section.data.map((field, idx) => {
                let value;
                if (field.value || typeof field.value === 'boolean') {
                    value = field.value;
                    switch (field.dataType) {
                        case 'text':
                            value = renderTextValue(value);
                            break;
                        case 'integer':
                        case 'decimal':
                            value = getNumberAsLocaleNumber(value);
                            break;
                        case 'percent':
                            value = field.value
                                ? `${getNumberAsLocaleNumber(field.value, {
                                      useGrouping: false,
                                      minimumFractionDigits: 2,
                                  })}%`
                                : '-';
                            break;
                        case 'relatedValueList':
                        case 'singleValueList':
                        case 'fuzzySearchSingle': {
                            if (field.value === '-1') {
                                value = '';
                                break;
                            }
                            const list =
                                extraFieldsSchemaMap?.[entity.extraFieldName]?.[
                                    field.id
                                ]?.valueListName?.toLowerCase();
                            switch (list) {
                                case 'viewusuariosfullname':
                                case 'viewcontactosfullname':
                                case 'empresas':
                                case 'expedientes': {
                                    const id = Number(field.value);
                                    value = (
                                        <EntityChip
                                            key={idx}
                                            entity={list}
                                            id={id}
                                            name={field.label}
                                            remotePopover
                                            isDismissible={false}
                                        />
                                    );
                                    break;
                                }
                                default:
                                    value = field.label;
                                    break;
                            }
                            break;
                        }
                        case 'multipleValueList':
                        case 'fuzzySearchMultiple':
                            value = field.value ? renderMultipleValueList(field.label) : '-';
                            break;
                        case 'bool':
                            value = getBackendBoolean(field.value)
                                ? getLiteral('common_yes')
                                : getLiteral('common_no');
                            break;
                        case 'currency': {
                            const [number, currencyName] = String(value).split(' ');
                            value = getNumberAsCurrency(
                                number,
                                data.symbolCurrency,
                                false,
                                false,
                                currencyName,
                            );
                            break;
                        }
                        case 'date':
                            value = getMomentFromDateBackend(value);
                            value = formatDateWithTimeOrNot(value, field?.includeTime);
                            break;
                        default:
                            break;
                    }
                }
                return {
                    ...field,
                    value,
                    description: (
                        <Text className="featured-fields__row__description-table">
                            {field.descripcion}
                        </Text>
                    ),
                };
            });
            const isHidden = sectionData.filter((field) => !field.hiddenByDynamism).length === 0;
            return { ...finalSection, isHidden, data: sectionData };
        });
    }, [
        allExtraFields,
        data?.symbolCurrency,
        entity?.extraFieldName,
        extraFieldsSchemaMap,
        renderMultipleValueList,
        renderTextValue,
    ]);

    const setFields = useCallback((sections) => {
        if (!sections) return;
        const fields = [];
        let nextFeaturedIds = {};
        sections.forEach((section) => fields.push(...section.data));
        fields.forEach((field) => {
            if (getBackendBoolean(field.isRelevant)) {
                nextFeaturedIds = { ...nextFeaturedIds, [field.id]: true };
            }
        });
        featuredFieldsRef.current = fields.filter((field) => getBackendBoolean(field.isRelevant));
        setFeaturedFieldsIds(nextFeaturedIds);
    }, []);

    useEffect(() => {
        if (!refresh) return;
        setIsLoading(true);
        const manager = Context.entityManager.getEntitiesManager(entity);
        Context.entityDetailManager
            .getExtraFields(entity, manager, entityId, setDynamicAsHidden)
            .then((response) => {
                setExtraFields(response?.extraFields ?? []);
                setIsLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setExtraFields(entityExtrafields);
                setIsLoading(false);
            });
        setRefresh(false);
    }, [entity, entityExtrafields, entityId, refresh, setDynamicAsHidden]);

    useEffect(() => {
        setExtraFields(entityExtrafields);
    }, [entityExtrafields]);

    useEffect(() => {
        setFields(finalSections);
    }, [finalSections, setFields]);

    const onChangeFeatured = useCallback(
        (field, isRelevant) => {
            if (isRelevant) {
                logEvent({
                    event: entity?.trueName,
                    submodule: 'extraFieldsModal',
                    functionality: 'favorite',
                });
                featuredFieldsRef.current.push(field);
            } else {
                logEvent({
                    event: entity?.trueName,
                    submodule: 'extraFieldsModal',
                    functionality: 'unfavorite',
                });
                featuredFieldsRef.current = featuredFieldsRef.current.filter(
                    (f) => f.id !== field.id,
                );
            }
            setFeaturedFieldsIds((prev) => ({ ...prev, [field.id]: isRelevant }));
        },
        [entity?.trueName],
    );

    const onConfirm = useCallback(() => {
        setIsLoading(true);
        const ids = featuredFieldsRef.current.map((field) => field.id);
        Context.domainManager
            .setRelevantFields({
                extraFieldNames: ids,
                fieldsEntity: entity?.dbName,
            })
            .then(() => {
                setIsModalOpen(false);
                setIsLoading(false);
                setRefresh(true);
            })
            .catch((error) => {
                console.error(error);
                setIsModalOpen(false);
                setIsLoading(false);
                setRefresh(true);
            });
    }, [entity?.dbName]);

    const onCancel = useCallback(() => {
        setIsModalOpen(false);
        setFields(finalSections);
    }, [finalSections, setFields]);

    const openModal = useCallback(() => {
        logEvent({
            event: entity?.trueName,
            submodule: 'infoWidget',
            functionality: 'showExtraFields',
        });
        setIsModalOpen(true);
    }, [entity?.trueName]);

    const onClickInput = useCallback(() => {
        logEvent({
            event: entity?.trueName,
            submodule: 'extraFieldsModal',
            functionality: 'search',
        });
    }, [entity?.trueName]);

    return {
        allExtraFields,
        featuredIds,
        featuredFieldsRef,
        finalSections,
        isLoading,
        isModalOpen,
        setRefresh,
        openModal,
        onChangeFeatured,
        onConfirm,
        onCancel,
        onClickInput,
    };
}
